import { useEffect, useState } from "react";
import { Activity, Database, Server, Mail, Webhook, Coins, AlertTriangle, RefreshCcw, CheckCircle2, XCircle, Send, ExternalLink, Download, Users, Copy, Store, Plus } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/lib/api";

const Dot = ({ ok }) => (
  <span
    className={`inline-block h-2 w-2 rounded-full ${
      ok ? "bg-green-500" : "bg-red-500"
    }`}
  />
);

const StatusCard = ({ icon: Icon, label, ok, children, testid }) => (
  <div
    data-testid={testid}
    className="bg-white rounded-xl border border-gray-200 p-5 shadow-[0_2px_12px_rgba(0,0,0,0.04)]"
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="h-8 w-8 rounded-lg bg-gray-50 text-gray-700 flex items-center justify-center">
          <Icon size={14} />
        </span>
        <div className="text-sm font-semibold text-gray-900">{label}</div>
      </div>
      <Dot ok={ok} />
    </div>
    <div className="mt-3 text-xs text-gray-600 space-y-1">{children}</div>
  </div>
);

export default function AdminPanel({ onResetDemo }) {
  const [status, setStatus] = useState(null);
  const [events, setEvents] = useState([]);
  const [demoLeads, setDemoLeads] = useState({ rows: [], summary: null });
  const [demoLeadsResending, setDemoLeadsResending] = useState({});
  const [retailers, setRetailers] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [retailerToggling, setRetailerToggling] = useState({});
  const [loading, setLoading] = useState(true);
  const [confirmReset, setConfirmReset] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, ev, leads, rs] = await Promise.all([
        api.get("/admin/system_status"),
        api.get(
          `/integrations/woocommerce/debug/events?token=${encodeURIComponent(
            "AUTHERA_DEMO_SECRET",
          )}`,
        ),
        api.get("/admin/demo_leads").catch(() => ({ data: { rows: [], summary: null } })),
        api.get("/admin/retailers").catch(() => ({ data: { rows: [] } })),
      ]);
      setStatus(s.data);
      setEvents(ev.data?.events || []);
      setDemoLeads({
        rows: leads.data?.rows || [],
        summary: leads.data?.summary || null,
      });
      setRetailers(rs.data?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load admin status");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success(`${label} copied`))
      .catch(() => toast.error(`Could not copy ${label}`));
  };

  const onToggleRetailer = async (rid, active) => {
    setRetailerToggling((s) => ({ ...s, [rid]: true }));
    try {
      await api.patch(`/admin/retailers/${rid}`, { active });
      toast.success(`${active ? "Activated" : "Deactivated"} retailer ${rid}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setRetailerToggling((s) => ({ ...s, [rid]: false }));
    }
  };

  const onCreateRetailer = async (payload) => {
    setCreating(true);
    try {
      await api.post("/admin/retailers", payload);
      toast.success(`Retailer ${payload.retailer_id} created`);
      setCreateOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  const onResendDemoLead = async (leadId, email) => {
    setDemoLeadsResending((s) => ({ ...s, [leadId]: true }));
    try {
      await api.post(`/admin/demo_leads/${leadId}/resend`);
      toast.success(`Sample resent to ${email}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    } finally {
      setDemoLeadsResending((s) => ({ ...s, [leadId]: false }));
    }
  };

  const onExportDemoLeadsCsv = () => {
    const rows = demoLeads.rows || [];
    const header = ["Email", "Submitted", "Sample Cert", "Tx Hash", "Email Sent", "User Agent", "IP"];
    const escape = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv =
      header.join(",") +
      "\n" +
      rows
        .map((r) =>
          [
            r.email,
            r.submitted_at,
            r.sample_certificate_id,
            r.sample_tx_hash,
            r.email_sent ? "yes" : "no",
            r.user_agent,
            r.ip,
          ]
            .map(escape)
            .join(","),
        )
        .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `authera-demo-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    load();
  }, []);

  if (loading || !status)
    return (
      <div className="mt-8 px-5 py-12 text-center text-gray-500" data-testid="admin-loading">
        Loading admin panel…
      </div>
    );

  const lastWebhook = status?.woocommerce?.last_webhook_at;
  const lastMint = status?.last_mint?.minted_at;
  const lastEmail = status?.last_email?.certificate_email_sent_at;

  // Outcome → display config. Centralised so the System Status card and the
  // Ingest Events table render the same human-friendly label and styling.
  const OUTCOME_DISPLAY = {
    processed: {
      label: "Processed",
      className: "text-green-700",
      tooltip: "Webhook received and processed successfully.",
    },
    all_duplicates: {
      label: "Duplicate Event (Already Processed)",
      className: "text-gray-600",
      tooltip:
        "This event was received more than once and safely ignored.",
    },
  };
  const outcomeMeta = (key) =>
    OUTCOME_DISPLAY[key] || {
      label: key || "—",
      className: key ? "text-red-700" : "text-gray-500",
      tooltip: key ? "Webhook delivery did not complete cleanly." : "",
    };

  const lastOutcomeKey = status?.woocommerce?.last_webhook_outcome;
  const lastOutcomeMeta = outcomeMeta(lastOutcomeKey);

  return (
    <div data-testid="admin-panel" className="mt-8 space-y-8">
      {/* System Status */}
      <section>
        <div className="flex items-end justify-between mb-3">
          <h2 className="font-display font-semibold text-xl text-gray-900">System Status</h2>
          <button
            onClick={load}
            data-testid="admin-refresh"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-[#0055FF]"
          >
            <RefreshCcw size={12} /> Refresh
          </button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatusCard icon={Server} label="Backend" ok={status.backend?.ok} testid="status-backend">
            <div>API responding</div>
          </StatusCard>
          <StatusCard icon={Database} label="Database" ok={status.database?.ok} testid="status-database">
            <div>MongoDB ping</div>
          </StatusCard>
          <StatusCard icon={Coins} label="Blockchain (Amoy)" ok={status.chain?.ok} testid="status-chain">
            <div>Chain {status.chain?.chain_id ?? "—"} · Block {status.chain?.block_number ?? "—"}</div>
            <div className="font-mono truncate">{status.chain?.rpc_url}</div>
          </StatusCard>
          <StatusCard icon={Mail} label="Email (SMTP)" ok={status.smtp?.configured} testid="status-smtp">
            <div>{status.smtp?.host}</div>
            <div className="font-mono truncate">{status.smtp?.from}</div>
          </StatusCard>
          <StatusCard icon={Webhook} label="WooCommerce" ok={status.woocommerce?.configured} testid="status-woo">
            <div className="font-mono truncate">{status.woocommerce?.store_url}</div>
            <div>Buffer: {status.woocommerce?.events_in_buffer ?? 0} events</div>
          </StatusCard>
          <StatusCard
            icon={Webhook}
            label="Webhook Health"
            ok={!!lastWebhook && (lastOutcomeKey === "processed" || lastOutcomeKey === "all_duplicates")}
            testid="status-webhook"
          >
            <div data-testid="webhook-last-received">
              Last received:{" "}
              {lastWebhook ? new Date(lastWebhook).toLocaleString() : "—"}
            </div>
            <div data-testid="webhook-last-outcome">
              Last outcome:{" "}
              <span
                className={`font-semibold ${lastOutcomeMeta.className}`}
                title={lastOutcomeMeta.tooltip}
              >
                {lastOutcomeMeta.label}
              </span>
            </div>
            <div data-testid="webhook-24h-count">
              Last 24h: {status.woocommerce?.webhook_count_24h ?? 0} events
            </div>
          </StatusCard>
          <StatusCard icon={Activity} label="Recent Activity" ok={true} testid="status-recent">
            <div>Last webhook: {lastWebhook ? new Date(lastWebhook).toLocaleString() : "—"}</div>
            <div>Last issuance: {lastMint ? new Date(lastMint).toLocaleString() : "—"}</div>
            <div>Last email: {lastEmail ? new Date(lastEmail).toLocaleString() : "—"}</div>
          </StatusCard>
        </div>
      </section>

      {/* Ingest events */}
      <section>
        <div className="flex items-end justify-between mb-3">
          <h2 className="font-display font-semibold text-xl text-gray-900">
            Ingest Events
          </h2>
          <div className="text-xs text-gray-500">{events.length} in buffer</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          {events.length === 0 ? (
            <div className="px-5 py-12 text-center text-gray-500 text-sm">
              No webhook events recorded yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="ingest-events-table">
                <thead>
                  <tr className="bg-gray-50/70 text-left text-[11px] font-semibold tracking-wider uppercase text-gray-500 border-b border-gray-200">
                    <th className="px-5 py-3">When</th>
                    <th className="px-5 py-3">Order</th>
                    <th className="px-5 py-3">Outcome</th>
                    <th className="px-5 py-3 text-right">Created</th>
                    <th className="px-5 py-3 text-right">Fulfilled</th>
                    <th className="px-5 py-3 text-right">Revoked</th>
                    <th className="px-5 py-3 text-right">Duplicates</th>
                    <th className="px-5 py-3 text-right">Skipped</th>
                  </tr>
                </thead>
                <tbody>
                  {events.slice().reverse().map((e, i) => {
                    const r = e.response || {};
                    const meta = outcomeMeta(e.outcome);
                    return (
                      <tr key={i} className="border-b border-gray-100 last:border-0">
                        <td className="px-5 py-3 text-gray-600 font-mono text-xs">
                          {e.at ? new Date(e.at).toLocaleString() : "—"}
                        </td>
                        <td className="px-5 py-3 font-mono text-xs">{e.order_id || "—"}</td>
                        <td
                          className={`px-5 py-3 font-semibold ${meta.className}`}
                          title={meta.tooltip}
                        >
                          {meta.label}
                        </td>
                        <td className="px-5 py-3 text-right">{r.created ?? 0}</td>
                        <td className="px-5 py-3 text-right">{r.fulfilled ?? 0}</td>
                        <td className="px-5 py-3 text-right">{r.revoked ?? 0}</td>
                        <td className="px-5 py-3 text-right">{r.duplicates ?? 0}</td>
                        <td className="px-5 py-3 text-right">{r.skipped ?? 0}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* Retailers */}
      <section data-testid="admin-retailers-section">
        <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
          <div>
            <h2 className="font-display font-semibold text-xl text-gray-900 inline-flex items-center gap-2">
              <Store size={16} className="text-[#0055FF]" />
              Retailers
            </h2>
            <div className="text-xs text-gray-500 mt-0.5">
              {retailers.length} total · manage tenants &amp; webhook credentials
            </div>
          </div>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            data-testid="retailer-create-open"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#0055FF] hover:bg-[#0044CC] px-3 py-1.5 rounded-md transition"
          >
            <Plus size={12} />
            New Retailer
          </button>
        </div>

        {createOpen && (
          <RetailerCreateForm
            onSubmit={onCreateRetailer}
            onCancel={() => setCreateOpen(false)}
            submitting={creating}
          />
        )}

        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          {retailers.length === 0 ? (
            <div className="px-5 py-12 text-center text-gray-500 text-sm" data-testid="retailers-empty">
              No retailers yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="retailers-table">
                <thead>
                  <tr className="bg-gray-50/70 text-left text-[11px] font-semibold tracking-wider uppercase text-gray-500 border-b border-gray-200">
                    <th className="px-5 py-3">Retailer</th>
                    <th className="px-5 py-3">ID</th>
                    <th className="px-5 py-3">Contact</th>
                    <th className="px-5 py-3">API Key</th>
                    <th className="px-5 py-3">Active</th>
                    <th className="px-5 py-3">Created</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {retailers.map((r) => (
                    <tr
                      key={r.retailer_id}
                      data-testid={`retailer-row-${r.retailer_id}`}
                      className="border-b border-gray-100 last:border-0"
                    >
                      <td className="px-5 py-3 text-gray-900 font-medium">{r.name}</td>
                      <td className="px-5 py-3 font-mono text-xs text-gray-700">
                        {r.retailer_id}
                      </td>
                      <td className="px-5 py-3 text-gray-600 text-xs">
                        {r.contact_email || "—"}
                      </td>
                      <td className="px-5 py-3 font-mono text-xs text-gray-500">
                        {r.api_key_masked}
                      </td>
                      <td className="px-5 py-3">
                        {r.active ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700">
                            <CheckCircle2 size={12} />
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500">
                            <XCircle size={12} />
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-gray-500 font-mono text-xs">
                        {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <div className="inline-flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => copyToClipboard(r.retailer_id, "Retailer ID")}
                            data-testid={`retailer-copy-id-${r.retailer_id}`}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-700 hover:text-[#0055FF] border border-gray-200 hover:border-[#0055FF] px-2 py-1 rounded transition"
                            title="Copy retailer ID"
                          >
                            <Copy size={11} />
                            ID
                          </button>
                          <button
                            type="button"
                            onClick={() => copyToClipboard((r.webhook_urls && r.webhook_urls.woocommerce) || r.webhook_url, "WooCommerce webhook URL")}
                            data-testid={`retailer-copy-webhook-${r.retailer_id}`}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-700 hover:text-[#0055FF] border border-gray-200 hover:border-[#0055FF] px-2 py-1 rounded transition"
                            title="Copy WooCommerce webhook URL (includes API key — admin-only)"
                          >
                            <Copy size={11} />
                            Woo
                          </button>
                          <button
                            type="button"
                            onClick={() => copyToClipboard((r.webhook_urls && r.webhook_urls.shopify) || "", "Shopify webhook URL")}
                            data-testid={`retailer-copy-shopify-${r.retailer_id}`}
                            disabled={!(r.webhook_urls && r.webhook_urls.shopify)}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-700 hover:text-[#0055FF] border border-gray-200 hover:border-[#0055FF] px-2 py-1 rounded transition disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Copy Shopify webhook URL (includes API key — admin-only)"
                          >
                            <Copy size={11} />
                            Shopify
                          </button>
                          <button
                            type="button"
                            onClick={() => onToggleRetailer(r.retailer_id, !r.active)}
                            disabled={!!retailerToggling[r.retailer_id]}
                            data-testid={`retailer-toggle-${r.retailer_id}`}
                            className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded transition disabled:opacity-60 disabled:cursor-wait ${
                              r.active
                                ? "text-gray-700 hover:text-red-700 border border-gray-200 hover:border-red-300"
                                : "text-white bg-green-600 hover:bg-green-700"
                            }`}
                          >
                            {r.active ? "Deactivate" : "Activate"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* Demo Leads */}
      <section data-testid="admin-demo-leads-section">
        <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
          <div>
            <h2 className="font-display font-semibold text-xl text-gray-900 inline-flex items-center gap-2">
              <Users size={16} className="text-[#0055FF]" />
              Demo Leads
            </h2>
            {demoLeads.summary && (
              <div className="text-xs text-gray-500 mt-0.5">
                {demoLeads.summary.total} total · {demoLeads.summary.sent} delivered · showing last {demoLeads.summary.shown}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onExportDemoLeadsCsv}
            disabled={!demoLeads.rows.length}
            data-testid="demo-leads-export-csv"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700 hover:text-[#0055FF] border border-gray-200 hover:border-[#0055FF] px-2.5 py-1.5 rounded-md transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download size={12} />
            Export CSV
          </button>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          {demoLeads.rows.length === 0 ? (
            <div
              className="px-5 py-12 text-center text-gray-500 text-sm"
              data-testid="demo-leads-empty"
            >
              No demo leads captured yet. Visitors who submit the{" "}
              <code className="font-mono text-xs bg-gray-50 px-1 rounded">/demo</code>{" "}
              form will appear here.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="demo-leads-table">
                <thead>
                  <tr className="bg-gray-50/70 text-left text-[11px] font-semibold tracking-wider uppercase text-gray-500 border-b border-gray-200">
                    <th className="px-5 py-3">Email</th>
                    <th className="px-5 py-3">Submitted</th>
                    <th className="px-5 py-3">Sample Cert</th>
                    <th className="px-5 py-3">Email Status</th>
                    <th className="px-5 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {demoLeads.rows.map((lead) => {
                    const verifyHref = lead.sample_tx_hash
                      ? `https://app.autheraapp.com/verify/${lead.sample_tx_hash.startsWith("0x") ? lead.sample_tx_hash : "0x" + lead.sample_tx_hash}`
                      : null;
                    return (
                      <tr
                        key={lead.id}
                        data-testid={`demo-lead-row-${lead.id}`}
                        className="border-b border-gray-100 last:border-0"
                      >
                        <td className="px-5 py-3 text-gray-900 font-medium">
                          {lead.email}
                        </td>
                        <td className="px-5 py-3 text-gray-600 font-mono text-xs">
                          {lead.submitted_at
                            ? new Date(lead.submitted_at).toLocaleString()
                            : "—"}
                        </td>
                        <td className="px-5 py-3 font-mono text-xs text-gray-700">
                          {lead.sample_certificate_id || "—"}
                        </td>
                        <td className="px-5 py-3">
                          {lead.email_sent ? (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700">
                              <CheckCircle2 size={12} />
                              Delivered
                            </span>
                          ) : (
                            <span
                              className="inline-flex items-center gap-1 text-xs font-semibold text-red-700"
                              title={lead.email_error || ""}
                            >
                              <XCircle size={12} />
                              Failed
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-right">
                          <div className="inline-flex items-center gap-2">
                            {verifyHref && (
                              <a
                                href={verifyHref}
                                target="_blank"
                                rel="noreferrer noopener"
                                data-testid={`demo-lead-verify-${lead.id}`}
                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-700 hover:text-[#0055FF] border border-gray-200 hover:border-[#0055FF] px-2 py-1 rounded transition"
                              >
                                <ExternalLink size={11} />
                                Open
                              </a>
                            )}
                            <button
                              type="button"
                              onClick={() => onResendDemoLead(lead.id, lead.email)}
                              disabled={!!demoLeadsResending[lead.id]}
                              data-testid={`demo-lead-resend-${lead.id}`}
                              className="inline-flex items-center gap-1 text-[11px] font-semibold text-white bg-[#0055FF] hover:bg-[#0044CC] px-2 py-1 rounded transition disabled:opacity-60 disabled:cursor-wait"
                            >
                              <Send size={11} />
                              {demoLeadsResending[lead.id] ? "Sending…" : "Resend"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* Reset Demo Safety */}
      <section data-testid="admin-reset-section">
        <h2 className="font-display font-semibold text-xl text-gray-900 mb-3">
          Reset Demo
        </h2>
        <div className="bg-white border border-blue-200 rounded-xl p-4 mb-4">
          <div className="flex items-start gap-3">
            <Mail size={16} className="text-[#0055FF] shrink-0 mt-0.5" />
            <div className="text-sm text-gray-700 leading-relaxed">
              <span className="font-semibold text-gray-900">Password reset:</span>{" "}
              Password reset is handled by rotating <code className="font-mono text-xs bg-blue-50 text-[#0055FF] px-1 py-0.5 rounded">ADMIN_PASSWORD</code> in environment settings and redeploying. Existing sessions are invalidated automatically.
            </div>
          </div>
        </div>
        <div className="bg-white border border-amber-200 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-gray-900">
                Safe Reset — only seed/demo records are deleted
              </div>
              <ul className="mt-2 text-sm text-gray-600 space-y-1.5 list-disc list-inside">
                <li className="text-green-700"><CheckCircle2 size={12} className="inline -mt-0.5" /> Real WooCommerce records (source = woocommerce) are <strong>preserved</strong></li>
                <li className="text-green-700"><CheckCircle2 size={12} className="inline -mt-0.5" /> Manually uploaded receipts are <strong>preserved</strong></li>
                <li className="text-red-700"><XCircle size={12} className="inline -mt-0.5" /> Seed demo records (source = seed or null) are deleted and re-seeded</li>
              </ul>
              <div className="mt-4 flex items-center gap-3">
                {confirmReset ? (
                  <>
                    <button
                      onClick={async () => {
                        setConfirmReset(false);
                        await onResetDemo();
                      }}
                      data-testid="admin-reset-confirm"
                      className="bg-red-600 text-white text-xs font-semibold px-3 py-2 rounded-md hover:bg-red-700"
                    >
                      Yes, reset demo data
                    </button>
                    <button
                      onClick={() => setConfirmReset(false)}
                      data-testid="admin-reset-cancel"
                      className="text-xs font-medium text-gray-600 hover:text-gray-900"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setConfirmReset(true)}
                    data-testid="admin-reset-trigger"
                    className="bg-white border border-gray-300 text-gray-900 text-xs font-semibold px-3 py-2 rounded-md hover:border-red-500 hover:text-red-600 transition"
                  >
                    Reset Demo Data
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}


/**
 * Inline "create retailer" form used inside the Retailers section. Collapses
 * after submit (via onSubmit resolving) or cancel.
 */
function RetailerCreateForm({ onSubmit, onCancel, submitting }) {
  const [name, setName] = useState("");
  const [retailerId, setRetailerId] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [active, setActive] = useState(true);

  const handle = (e) => {
    e.preventDefault();
    const payload = {
      name: name.trim(),
      retailer_id: retailerId.trim().toLowerCase(),
      contact_email: contactEmail.trim() || null,
      active,
    };
    onSubmit(payload);
  };

  return (
    <form
      onSubmit={handle}
      data-testid="retailer-create-form"
      className="mb-3 bg-white border border-[#0055FF]/30 rounded-xl p-5 shadow-sm"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-[10px] font-semibold tracking-[0.18em] uppercase text-gray-600 mb-1.5">
            Retailer Name
          </label>
          <input
            data-testid="retailer-create-name"
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Rolex Fifth Avenue"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0055FF]/30 focus:border-[#0055FF]"
          />
        </div>
        <div>
          <label className="block text-[10px] font-semibold tracking-[0.18em] uppercase text-gray-600 mb-1.5">
            Retailer ID / Slug
          </label>
          <input
            data-testid="retailer-create-id"
            type="text"
            required
            value={retailerId}
            onChange={(e) => setRetailerId(e.target.value.toLowerCase())}
            pattern="[a-z0-9][a-z0-9-]{1,46}[a-z0-9]"
            placeholder="rolex-fifth-ave"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0055FF]/30 focus:border-[#0055FF]"
          />
          <div className="mt-1 text-[10px] text-gray-500">
            Lowercase letters, digits, hyphens. 3–48 chars.
          </div>
        </div>
        <div>
          <label className="block text-[10px] font-semibold tracking-[0.18em] uppercase text-gray-600 mb-1.5">
            Contact Email
          </label>
          <input
            data-testid="retailer-create-email"
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="ops@rolexfifthave.com"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0055FF]/30 focus:border-[#0055FF]"
          />
        </div>
        <div className="flex items-end">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              data-testid="retailer-create-active"
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-[#0055FF] focus:ring-[#0055FF]/30"
            />
            Active
          </label>
        </div>
      </div>
      <div className="mt-5 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="text-xs font-semibold text-gray-700 hover:text-gray-900 px-3 py-2 rounded-md transition"
        >
          Cancel
        </button>
        <button
          type="submit"
          data-testid="retailer-create-submit"
          disabled={submitting}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#0055FF] hover:bg-[#0044CC] px-3 py-2 rounded-md transition disabled:opacity-60 disabled:cursor-wait"
        >
          {submitting ? "Creating…" : "Create Retailer"}
        </button>
      </div>
    </form>
  );
}
